// ===========================================
// Assign Registration Fee to a Child (#374)
// ===========================================
// A fee paid before fees were per child has no child. This picks one. The server
// refuses a fee already credited on an invoice and a child who already has a fee;
// both come back as 409 codes and are shown kindly (switch on the code, not the text).

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useAssignRegistrationPaymentStudent } from '../../../hooks/useFamilyPayments';
import { apiErrorCode, apiErrorDetails, apiErrorMessage } from '../../../utils/apiError';

interface AssignRegistrationFeeDialogProps {
  open: boolean;
  onClose: () => void;
  familyId: string;
  /** The fee being assigned; null while closed. */
  feeId: string | null;
  childOptions: { id: string; name: string }[];
  /**
   * The fee is already credited on an invoice (the report's Check / Unassigned rows): the child can
   * be CONFIRMED but not changed, so only the fee's current child is offered. `invoiceNumber` is null
   * for a viewer without invoice access.
   */
  credit?: { invoiceNumber: string | null } | null;
  /** The child the fee is currently on (a Check row); null/absent for an Unassigned fee. */
  currentStudentId?: string | null;
}

export function assignErrorText(error: unknown): string {
  switch (apiErrorCode(error)) {
    case 'REGISTRATION_FEE_CREDITED': {
      const invoiceNumber = apiErrorDetails<{ invoiceNumber?: string }>(error)?.invoiceNumber;
      return `This fee has already been credited on ${
        invoiceNumber ? `invoice ${invoiceNumber}` : 'an invoice'
      }, so it can no longer be moved to another child.`;
    }
    case 'STUDENT_ALREADY_HAS_FEE':
      return 'That child already has a registration fee on record. Choose a different child.';
    default:
      return apiErrorMessage(error, 'Could not assign the registration fee');
  }
}

export default function AssignRegistrationFeeDialog({
  open,
  onClose,
  familyId,
  feeId,
  childOptions,
  credit,
  currentStudentId,
}: AssignRegistrationFeeDialogProps) {
  const creditedOn = credit ? (credit.invoiceNumber ? `Credited on ${credit.invoiceNumber}` : 'Credited on an invoice') : null;
  const options = credit && currentStudentId ? childOptions.filter((c) => c.id === currentStudentId) : childOptions;
  const [studentId, setStudentId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mutation = useAssignRegistrationPaymentStudent();

  useEffect(() => {
    if (open) {
      setStudentId('');
      setError(null);
    }
  }, [open, feeId]);

  const handleSubmit = async () => {
    if (!feeId || !studentId) return;
    setError(null);
    try {
      await mutation.mutateAsync({ familyId, id: feeId, studentId });
      onClose();
    } catch (e) {
      setError(assignErrorText(e));
    }
  };

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Assign to child</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {creditedOn
            ? `${creditedOn}: confirm the child this registration fee was paid for. It cannot be moved to another child.`
            : 'Choose the child this registration fee was paid for.'}
        </Typography>
        <FormControl fullWidth size="small" disabled={mutation.isPending}>
          <InputLabel id="assign-fee-child-label">Child</InputLabel>
          <Select
            labelId="assign-fee-child-label"
            label="Child"
            value={studentId}
            onChange={(e) => setStudentId(e.target.value as string)}
          >
            {options.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending} sx={{ minHeight: { xs: 44 } }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!studentId || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ minHeight: { xs: 44 } }}
        >
          {mutation.isPending ? 'Assigning...' : creditedOn ? 'Confirm' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
